<template>
    <nav class="app-menu">
        <results-modal class="results-modal" :term="searchQuery"/>
        <div class="search-bar">
            <search-field name="search"
                          placeholder="Search"
                          label="Search"
                          inverse-colors
                          ref="searchField"
                          v-model="searchQuery"/>
        </div>
        <div class="top-menu-links">
            <router-link class="top-menu-link" :to="{ name: ROUTE_DOCS }">Docs</router-link>
            <router-link class="top-menu-link" :to="{ name: ROUTE_TYPES }">Types</router-link>
            <a class="top-menu-link top-menu-link-external"
               href="https://github.com/messengerpeople"
               target="_blank"
               rel="noopener">Github</a>
            <a class="top-menu-link top-menu-link-external"
               href="https://www.messengerpeople.dev"
               target="_blank"
               rel="noopener">Home</a>
        </div>

        <div class="page-links">
            <router-link v-for="page in pages" :key="page.slug" :to="pageRoute(page)" class="page-link">
                {{ page.title }}
            </router-link>
        </div>

        <div class="resource-links">
            <router-link v-for="resource in resources" :key="resource.id"
                         :to="resourceRoute(resource)"
                         class="resource-link">
                <span class="resource-link-label">{{ resource.name }}</span>
                <div class="endpoint-links">
                    <router-link v-for="endpoint in resource.endpoints"
                                 :key="endpoint.id"
                                 :to="endpointRoute(resource, endpoint)"
                                 class="endpoint-link">
                        <span class="endpoint-link-label">{{ endpoint.name }}</span>
                    </router-link>
                </div>
            </router-link>
        </div>

        <footer class="menu-footer">
            <span>
                Made with <span style="color:#ffb9a0">❤</span> by
                <a class="home-link footer-link" href="https://www.messengerpeople.com">MessengerPeople</a>
            </span>
            <div class="footer-links">
                <a class="footer-link" href="https://www.messengerpeople.dev" target="_blank">
                    Homepage
                </a>
                <a class="footer-link" href="https://www.messengerpeople.dev/legal-information" target="_blank">
                    Legal
                </a>
                <a class="footer-link" href="https://www.messengerpeople.dev/tos" target="_blank">
                    Terms and Conditions
                </a>
                <a class="footer-link" href="https://www.messengerpeople.dev/privacy" target="_blank">
                    Privacy Policy
                </a>
            </div>
        </footer>
    </nav>
</template>

<!--suppress JSValidateTypes -->
<script>
  import { eventBus }                                            from '../../modules/eventBus';
  import { ROUTE_DOCS, ROUTE_PAGE, ROUTE_RESOURCE, ROUTE_TYPES } from '../../router';
  import SearchField                                             from '../Forms/SearchField';
  import ResultsModal                                            from '../ResultsModal';

  export default {
    name:       'AppMenu',
    components: { ResultsModal, SearchField },

    data () {
      return {
        searchQuery: '',
        ROUTE_DOCS,
        ROUTE_RESOURCE,
        ROUTE_TYPES,
      };
    },

    computed: {

      /**
       * @returns {Resource[]}
       */
      resources () {
        return this.$api.resources;
      },

      /**
       * @return {Page[]}
       */
      pages () {
        return this.$api.pages;
      },
    },

    mounted () {
      eventBus.$on('shortcut:search', () => {
        this.$refs.searchField.focus();
      });
    },

    methods: {
      /**
       * Builds a page route
       *
       * @param  {Page}                                         page    Page to route to
       * @return {{name: string, params: { slug: string } }}            Page route
       */
      pageRoute ( page ) {
        return {
          name:   ROUTE_PAGE,
          params: {
            slug: page.slug,
          },
        };
      },

      /**
       * Builds a resource route
       *
       * @param  {Resource}                                     resource    Resource to route to
       * @return {{name: string, params: { slug: string } }}                Resource route
       */
      resourceRoute ( resource ) {
        return {
          name:   ROUTE_RESOURCE,
          params: {
            slug: resource.slug,
          },
        };
      },

      /**
       * Builds an endpoint route
       *
       * @param  {Resource}                                                 resource    Resource to route to
       * @param  {Endpoint}                                                 endpoint    Endpoint to route to
       * @return {{name: string, params: { slug: string }, hash: string }}              Endpoint route
       */
      endpointRoute ( resource, endpoint ) {
        return {
          name:   ROUTE_RESOURCE,
          params: {
            slug: resource.slug,
          },
          hash:   `#endpoint-${ endpoint.slug }`,
        };
      },
    },
  };
</script>

<style scoped>
    .app-menu {
        overflow-y: scroll;
        max-height: 100%;
        background: inherit;
    }

    .top-menu-links {
        display:         flex;
        justify-content: space-between;
        padding:         0 1rem;
    }

    .page-links,
    .resource-links,
    .endpoint-links {
        display:        flex;
        flex-direction: column;
    }

    .page-links,
    .resource-links {
        margin-top: 2rem;
    }

    .top-menu-link,
    .resource-link,
    .page-link,
    .endpoint-link {
        text-decoration: none;
        color:           inherit;
        opacity:         0.5;
        user-select:     none;
        outline:         none;
        transition:      all 250ms;
    }

    .top-menu-link {
        padding:       0.5rem 1rem;
        border-radius: 2rem;
    }

    .top-menu-link:hover,
    .top-menu-link:focus {
        opacity:    0.75;
        background: linear-gradient(45deg, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.2));
        box-shadow: inset 1px -1px 1px rgba(0, 0, 0, 0.1);
        color:      var(--color-white);
    }

    .top-menu-link.router-link-exact-active {
        opacity:    1;
        background: var(--color-white);
        color:      var(--color-primary);
    }

    .resource-link,
    .page-link,
    .endpoint-link {
        padding: 0.5rem 1rem;
    }

    .resource-link:hover,
    .resource-link:focus,
    .page-link:hover,
    .page-link:focus,
    .endpoint-link:hover,
    .endpoint-link:focus {
        opacity: 0.85;
    }

    .resource-link.router-link-exact-active,
    .page-link.router-link-exact-active,
    .endpoint-link.router-link-exact-active {
        opacity:    1;
        box-shadow: -0.75rem 0 0 -0.5rem var(--color-white);
    }

    .search-bar {
        position:      sticky;
        top:           0;
        background:    inherit;
        margin-bottom: 1rem;
        padding:       1rem;
        z-index:       1;
    }

    .menu-footer {
        display:         flex;
        flex-direction:  column;
        justify-content: center;
        align-items:     center;
        margin-top:      3rem;
        padding:         1rem;
        font-size:       0.85rem;
        opacity:         1;
        color:           rgba(255, 255, 255, 0.75);
        text-shadow:     -1px -1px 0 rgba(0, 0, 0, 0.05);
        background:      linear-gradient(165deg, rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.15));
    }

    .menu-footer .home-link {
        text-decoration: none;
    }

    .menu-footer .footer-links {
        display:         flex;
        flex-wrap:       wrap;
        justify-content: center;
        margin-top:      2rem;
    }

    .menu-footer .footer-links .footer-link {
        display: inline-block;
        padding: 0.25rem 0.45rem;
    }

    .menu-footer .footer-link {
        color: inherit;
    }

    .app-menu .results-modal {
        top:        8.75rem;
        max-height: calc(100vh - 10rem);
        overflow: auto;
    }
</style>
